import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { from, Observable, of, switchMap, throwError } from 'rxjs';
import { JwtAuthService } from '../services/auth/jwt-auth.service';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { CryptoDecryptService } from '../services/crypto-decrypt.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshToken$?: Observable<string>;

  constructor(
    private cryptoDecryptService: CryptoDecryptService,
    private jwtAuth: JwtAuthService) {
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(this.addTokenHeader(req)).pipe(
      switchMap((event: HttpEvent<any>) => {
        return this.fixBodyResponse(event, this.jwtAuth.getJwtToken());
      }),
      catchError((error: HttpErrorResponse) => {
        if (this.isTokenRequest(req.url)) {
          return this.formatError(error);
        }

        if (error && error.status === 401) {
          const hasRefreshToken = !!this.jwtAuth.getJwtRefreshToken();
          if (!hasRefreshToken) {
            this.jwtAuth.logout(true);
            return this.formatError(error);
          }

          return this.getOrCreateRefreshToken$().pipe(
            switchMap(() => {
              return next.handle(this.addTokenHeader(req)).pipe(
                switchMap((event: HttpEvent<any>) => {
                  return this.fixBodyResponse(event, this.jwtAuth.getJwtToken());
                }),
              );
            }),
            catchError((refreshError: HttpErrorResponse) => {
              return this.formatError(refreshError);
            }),
          );
        }

        return this.formatError(error);
      }),
    );
  }

  fixBodyResponse(event: HttpEvent<any>, token: string | null) {
    if (
      event instanceof HttpResponse
      && ['rbe1c2', 'rbe1c1'].includes(event.headers.get('content-encoding'))
    ) {
      const encoding: any = event.headers.get('content-encoding');
      return from(
        this.cryptoDecryptService.decryptBody(event.body, token, encoding)
      ).pipe(
        map(decryptedBody => {
          return event.clone({
            body: decryptedBody,
          });
        }),
      );
    } else if (event instanceof HttpResponse) {
      let parsedBody: any;
      try {
        if (event.body instanceof ArrayBuffer) {
          // Se for ArrayBuffer → converte para string e tenta JSON
          const text = new TextDecoder().decode(event.body);
          parsedBody = JSON.parse(text);
        } else if (typeof event.body === 'string') {
          // Se for string → tenta JSON.parse
          parsedBody = JSON.parse(event.body);
        } else {
          // Se já for objeto/json ou outro tipo → usa direto
          parsedBody = event.body;
        }
      } catch (e) {
        console.warn('⚠️ Falha ao parsear body, retornando cru:', e);
        parsedBody = event.body;
      }

      return of(event.clone({ body: parsedBody }));
    }

    return of(event);
  }

  formatError(error: HttpErrorResponse) {
    if (error?.error) {
      try {
        let parsedError: any;

        if (error.error instanceof ArrayBuffer) {
          // Decodifica ArrayBuffer -> string
          const decoded = new TextDecoder().decode(error.error);

          try {
            parsedError = JSON.parse(decoded);
          } catch {
            parsedError = decoded; // Se não for JSON, devolve como string
          }
        } else if (typeof error.error === 'string') {
          // Se já for string, tenta parsear como JSON
          try {
            parsedError = JSON.parse(error.error);
          } catch {
            parsedError = error.error; // Se não for JSON, mantém a string
          }
        } else {
          // Já é objeto ou outro tipo (ex.: JSON já pronto)
          parsedError = error.error;
        }

        // Retorna o mesmo HttpErrorResponse, apenas trocando o campo "error"
        return throwError(() => new HttpErrorResponse({ ...error, error: parsedError }));
      } catch (e) {
        console.warn('⚠️ Falha ao processar erro:', e);
        return throwError(() => error);
      }
    }
    return throwError(() => error);
  }

  addTokenHeader(req: HttpRequest<any>) {
    const token = this.jwtAuth.getJwtToken();
    const excludeUrlS3 = '.s3.us-east-2.amazonaws.com';
    const excludeToken = '/v1/token';
    if (
      token &&
      req.url.indexOf(excludeUrlS3) === -1 &&
      req.url.indexOf(excludeToken) === -1
    ) {
      return req.clone({
        setHeaders: {
          Authorization: `Bearer ${ token }`,
        },
        responseType: 'arraybuffer' as 'json'
      });
    }
    return req;
  }

  private isTokenRequest(url: string): boolean {
    return url.includes('/v1/token');
  }

  private getOrCreateRefreshToken$(): Observable<string> {
    if (!this.refreshToken$) {
      this.refreshToken$ = this.jwtAuth.refreshToken().pipe(
        map(() => {
          const refreshedToken = this.jwtAuth.getJwtToken();
          if (!refreshedToken) {
            throw new HttpErrorResponse({
              status: 401,
              statusText: 'Unauthorized',
              error: { message: 'Refresh token completed without access token' },
            });
          }

          return refreshedToken;
        }),
        catchError((refreshError: HttpErrorResponse) => {
          this.jwtAuth.logout(true);
          return throwError(() => refreshError);
        }),
        finalize(() => {
          this.refreshToken$ = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.refreshToken$;
  }
}
